import { validation } from 'utils/validation';
import {
    formSingleContentData,
    formSingleOptionData
} from 'state/reducers/embedsState/embedManagementState/contentStatus/content_status_state_structure';
import { pushUniqueElement, deleteElementByValue } from '@insticator/insticator-ui'; // iui-utils


// HELPER METHODS TO FORM A DEFAULT STATE DATA STRUCTURE

export const hasQBData = contentData => !!contentData && (contentData.contentType === 'QUIZ' || contentData.contentType === 'POLL');
export const hasStoriesData = contentData => !!contentData && contentData.contentType === 'STORY';

// Forms a status blob for all of the question options
// For new question forms 3 options
export const formOptionsData = optionsData => {
    const options = {};
    for (let idx=0; idx < (optionsData ? Object.keys(optionsData).length : 3); idx++) {
        let optionId = optionsData ? Object.keys(optionsData)[idx] : `${idx}`;
        options[optionId] = formSingleOptionData(optionsData ? optionsData[optionId] : null, idx);
    }
    return options;
};


/* ++++++++++ --------------- CUSTOM METHODS --------------- ++++++++++ */

// TRANSPILER
// Updates content status with the data received from the BE.
// Updates some of the contentStatus properties to account for received contents;
export const formContentsData = contentsData => {
    const contents = {};
    let lastQueuePosition = 0;
    let totalNumberOfContents = 0;
    let firstLiveContentId = null;
    let firstQueuePosition = contentsData.length;
    let totalNumberOfLiveContents = 0;
    for(let idx = 0; idx < contentsData.length; idx++) {
        const currentContentData = contentsData[idx];
        const contentId = currentContentData.contentUUID;
        lastQueuePosition = currentContentData.queuePosition !== null && currentContentData.queuePosition > lastQueuePosition ? currentContentData.queuePosition : lastQueuePosition;
        totalNumberOfContents += 1;
        if(currentContentData.queuePosition !== null && currentContentData.queuePosition <= firstQueuePosition) {
            firstLiveContentId = contentId;
            firstQueuePosition = currentContentData.queuePosition;
        }
        contents[contentId] = formSingleContentData({ contentData: currentContentData, contentProperties: currentContentData.contentProperties, contentId, type: null, position: null });
        if (currentContentData.contentLive) { totalNumberOfLiveContents += 1; }
    }
    let noLiveContents = totalNumberOfLiveContents === 0;
    return { contents, lastQueuePosition, totalNumberOfContents, firstLiveContentId, noLiveContents };
};

export const formAddOnsData = contentsAddOnData => ({
    userDataCollection: formUserDataCollectionData(contentsAddOnData && contentsAddOnData.userDataCollection)
});

const formUserDataCollectionData = data => ({
    dataPresent: !!data,
    contentUUID: !!data ? data.contentUUID : null,
    queuePosition: { value: !!data ? data.queuePosition : 7 , valid: true },
    publisherName: { value: !!data ? data.publisherName : '', valid: !!data ? !!data.publisherName : null },
    publisherPrivacyPolicyURL: { value: !!data ? data.publisherPrivacyPolicyURL : '', valid: !!data ? data.publisherPrivacyPolicyURL : null },
    backgroundColor: { value: !!data ? data.backgroundColor : 'rgba(48,48,48,0.8)', valid: true },
    backgroundImageURL: !!data ? data.backgroundImageURL : null,
    headline: { value: !!data ? data.headline : '', valid: !!data ? !!data.headline : null },
    body: { value: !!data ? data.body : '', valid: !!data ? !!data.body : null }
});

export const convertAddOnTypeToPropertyValue = type => {
    switch (type) {
        case 'EMAIL_COLLECTION':
        default:
            return 'userDataCollection'
    }
};

export const getUpdatedContents = ({ updatedContents, currentState }) => {
    let contents = JSON.parse(JSON.stringify(currentState.contents));
    let firstLiveContentId = currentState.firstLiveContentId;
    let selectedContent = currentState.selectedContent;

    // If FE generated ids for newly created contents assigned to firstLiveContentId or selectedContent,
    // Find their queue positions. The BE will assign new ids to new contents and will find them by their queue positions;
    let selectedContentPosition = !!selectedContent ? currentState.contents[selectedContent]['queuePosition'] : null;

    // update current contents data with the updated one
    for(let idx = 0; idx < updatedContents.length; idx++) {
        const currentContentData = updatedContents[idx];
        const currentPosition = currentContentData.queuePosition;
        const contentId = currentContentData.contentUUID;
        contents[contentId] = formSingleContentData({
            contentData: currentContentData,
            contentProperties: currentContentData.contentProperties,
            contentId,
            type: null,
            position: null
        });

        // insure updated content ids assigned to firstLiveContentId and selectedContent properties
        if(currentPosition === 0) firstLiveContentId = contentId;
        if(currentPosition !== null && currentPosition === selectedContentPosition) selectedContent = contentId;
    }

    // remove new content blobs since the call will return a content with the newly assigned id
    for(let jdx = 0; jdx < currentState.newContents.length; jdx++) {
        delete contents[currentState.newContents[jdx]]
    }
    return { contents, firstLiveContentId, selectedContent }
};

export const invalidateContent = contentData => {
    if(!!contentData){
        return contentData.contentType === 'story' ? _invalidateStoryContent(contentData) : _invalidateQBContent(contentData);
    }
    return null;
};

export const invalidateAddOn = (type, data) => {
    switch (type) {
        case 'EMAIL_COLLECTION':
        default:
            return invalidateEmailCollection(data);
    }
};

// backgroundColor: {value: "rgba(179, 34, 34, 0.8)", valid: true}
// backgroundImageURL: "https://d1mxva141a8fyb.cloudfront.net/client_add_ones_images/user_data_collection/86dd03a1-053f-4e3e-90e7-389070a0c62c/65ef9be5-8d00-4521-a830-16db43fe3f03"
// body: {value: "", valid: null}
// contentUUID: null
// headline: {value: "", valid: null}
// publisherName: {value: "jon", valid: null}
// publisherPrivacyPolicyURL: {value: "link", valid: null}
// queuePosition: {value: 1, valid: true}

const invalidateEmailCollection = data => {
    let invalidatedData = JSON.parse(JSON.stringify(data));
    invalidatedData.body.valid = !!invalidatedData.body.value;
    invalidatedData.headline.valid = !!invalidatedData.headline.value;
    invalidatedData.publisherName.valid = !!invalidatedData.publisherName.value;
    invalidatedData.publisherPrivacyPolicyURL.valid = !!validation.storyURL.test.test(invalidatedData.publisherPrivacyPolicyURL.value);
    return invalidatedData

}

const getDuplicateOptions = (type, options) => {
    if (['image1', 'text1', 'text2'].includes(type)) {
        let optionTexts = [];
        for (let contentId of Object.keys(options)) {
            optionTexts.push(options[contentId]['text']);
        }
        const uniqueOptionTexts = [...new Set(optionTexts)];
        if (uniqueOptionTexts.length !== optionTexts.length) {
            let uniqueOptions = {};
            for (let contentId of Object.keys(options)) {
                uniqueOptionTexts.forEach((text, i) => {
                    if (text === options[contentId]['text']) {
                        uniqueOptions[contentId] = options[contentId];
                        delete uniqueOptionTexts[i];
                    }
                });
            }
            return uniqueOptions;
        }
    }
    return false;
};

const _invalidateQBContent = contentData => {
    const content = JSON.parse(JSON.stringify(contentData));
    content.questionBased.isValid = false; // this method is called when we know that the content is not valid
    // isValid will be set back to null when content will be selected
    content.questionBased.isTitleValid = !!content.questionBased.isTitleValid; // will set content.isTitleValid to false if it is set to null
    content.questionBased.correctOptionSelected = content.contentType !== 'quiz'; // validation for correct option selection. This validation is not happening in individual components.
    const uniqueOptions = getDuplicateOptions(content.questionBased.type, content.questionBased.options);
    for(let contentId of Object.keys(content.questionBased.options)) {
        content.questionBased.options[contentId]['isValid'] = content.questionBased.options[contentId]['isValid'] instanceof Object ? false : !!content.questionBased.options[contentId]['isValid'];
        content.questionBased.correctOptionSelected = content.contentType === 'quiz' && content.questionBased.options[contentId]['correctOption'] ?
            true : content.questionBased.correctOptionSelected;
        if(uniqueOptions) content.questionBased.options[contentId]['isDuplicate'] = uniqueOptions[contentId] === undefined;
    }
    return content;
};

const _invalidateStoryContent = contentData => {
    const content = JSON.parse(JSON.stringify(contentData));
    content.story.isValid = false; // this method is called when we know that the content is not valid
    // isValid will be set back to null when content will be selected
    content.story.isTitleValid = !!content.story.isTitleValid; // will set content.story.isTitleValid to false if it is set to null
    content.story.isCTAValid = !!content.story.isCTAValid; // will set content.story.isCTAValid to false if it is set to null
    content.story.isURLValid = !!content.story.isURLValid;     // will set content.story.isURLValid to false if it is set to null
    return content;
};

export const getUpdatedStateParamsOnDelete = (contentsData, removedContentId, removedQueuePosition) =>{
    let lastQueuePosition = 0;
    let newSelectedContent = '0';
    const contents = {};
    for(let contentId of Object.keys(contentsData)) {
        contents[contentId] = contentsData[contentId];
        contents[contentId]['queuePosition'] = contentsData[contentId].queuePosition !== null ? contentsData[contentId].queuePosition < removedQueuePosition ? contentsData[contentId].queuePosition : contentsData[contentId].queuePosition - 1 : null;
        lastQueuePosition = contents[contentId].queuePosition !== null && contents[contentId].queuePosition > lastQueuePosition ? contents[contentId].queuePosition : lastQueuePosition;
        newSelectedContent = contents[contentId].queuePosition !== null && contents[contentId].queuePosition === lastQueuePosition ? contentId : newSelectedContent;
    }
    return { lastQueuePosition, newSelectedContent, contents }
};

export const setSelectedImage = (state, action) => {
    const newState = JSON.parse(JSON.stringify(state));
    const contentType = newState['contents'][newState.selectedContent]['contentType'] === 'story' ? 'story' : 'questionBased';
    newState['contentChangeDetected'] = true;
    newState['updatedContents'] = pushUniqueElement(newState['updatedContents'], newState.selectedContent);
    switch (newState['activeSelection']) {
        case 'background':
            newState['contents'][newState.selectedContent][contentType]['imageProvider'] = action.provider;
            newState['contents'][newState.selectedContent][contentType]['imageID'] = action.imageId;
            newState['contents'][newState.selectedContent][contentType]['backgroundImageURL'] = action.imageURL;
            newState['contents'][newState.selectedContent][contentType]['previewBackgroundImageURL'] = null;
            newState['activeSelection'] = 'none';
            return newState;
        default:
            if (newState['activeSelection'].includes('option')) {
                const optionId = newState['activeSelection'].slice(7);
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageProvider'] = action.provider;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageID'] = action.imageId;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageURL'] = action.imageURL;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['previewImageURL']= null;
                newState['activeSelection'] = 'none';
                return newState;
            }
            return state;
    }
};
export const setPreviewImage = (state, action) => {
    const newState = JSON.parse(JSON.stringify(state));
    const contentType = newState['contents'][newState.selectedContent]['contentType'] === 'story' ? 'story' : 'questionBased';
    switch (newState['activeSelection']) {
        case 'background':
            newState['contents'][newState.selectedContent][contentType]['previewBackgroundImageURL'] = action.imageURL;
            return newState;
        default:
            if (newState['activeSelection'].includes('option')) {
                const optionId = newState['activeSelection'].slice(7);
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['previewImageURL'] = action.imageURL;
                return newState;
            }
            return state;
    }
};

export const typeChangedFromOriginal = (originalType, currentType, newType) =>
    // Ignore type change for a new content.
    originalType === null ? false :
        // 1. Content type has actually changed. New type is different from the current one.
        contentTypeChanged(currentType, newType) &&
        // 2. Current type was the original one.
        !contentTypeChanged(originalType, currentType);

export const typeChangedToOriginal = (originalType, currentType, newType) =>
    // Ignore type change for a new content.
    originalType === null ? false :
        // 1. Content type has actually changed. New type is different from the current one.
        contentTypeChanged(currentType, newType) &&
        // 2. New content type is the original one
        !contentTypeChanged(originalType, newType);


export const contentTypeChanged = (baseType, contentType) => {
    switch (baseType) {
        case 'story':
            return contentType !== 'story';
        case 'poll':
        // fall through
        case 'quiz':
            return contentType === 'story';
        default:
            return false;
    }
};

export const getStateWithOfflineContent = (state, prevState) => {
    const newState = JSON.parse(JSON.stringify(state));
    const previousState = JSON.parse(JSON.stringify(prevState));
    const originalContentId = newState.selectedContent;

    // content state with all of the modifications. Properties of both questionBased and story types are populated.
    // currentSelectedContentState is a reference.
    const newContent = newState['contents'][originalContentId];

    // generate the new id to store a state of the originally received content separately from an updated content state
    const newContentId = generateId();

    // assign new contentId to the newly created content
    newContent.contentId = newContentId;

    // updated corresponding state references
    newState.selectedContent = newContentId;
    newState.firstLiveContentId = newContent.queuePosition === 0 ? newContentId : newState.firstLiveContentId;
    newState.totalNumberOfContents += 1;

    // track newly created content
    newState.newContents.push(newContentId);

    // set originally received content offline
    const originalContent = previousState['contents'][originalContentId];
    originalContent.contentLive = false;
    originalContent.queuePosition = null;

    // set id's to map the existing and a newly created contents
    originalContent.parentId = newContentId;
    newContent.offlineBlobId = originalContentId;

    // add stored copy of the the originally received content to the list of contents
    newState['contents'][newContentId] = newContent;
    newState['contents'][originalContentId] = originalContent;

    return newState;
};

export const finalizeContentTypeChange = state => {
    const newState = JSON.parse(JSON.stringify(state));
    const originalType = newState['contents'][newState.selectedContent]['originalType'];

    // content state with all of the modifications. Properties of both questionBased and story types are populated.
    // currentSelectedContentState is a reference.
    const currentSelectedContentState = newState['contents'][newState.selectedContent];

    // clear properties of the original content type
    const origialTypeSpecificProps =  _isQuestionBased(originalType) ? 'questionBased': 'story';
    currentSelectedContentState[origialTypeSpecificProps] = formSingleContentData({ contentData: null, contentProperties: null, contentId: null, type: 'quiz', position: 0 })[origialTypeSpecificProps];

    // remove the connection between the currently changed content and its offline blob
    const offlineBlobId = currentSelectedContentState['offlineBlobId'];
    newState['contents'][offlineBlobId]['parentId'] = null;
    currentSelectedContentState['offlineBlobId'] = null;
    currentSelectedContentState['originalType'] = null;

    return newState;
};

//This method is called if user switches back to initial content type before finalizing the content type change
export const removeOfflineBlob = state => {
    const newState = JSON.parse(JSON.stringify(state));
    // id of the content originally received from the BE
    const originalContentId = newState['contents'][newState.selectedContent]['offlineBlobId'];
    // id of the new content created in place of the original one to handle content type change
    const newContentId = newState.selectedContent;

    // currentContentState has the data of both states
    // Note: The data of the initial type erases only if the type change is finalized
    const currentContentState = newState['contents'][newState.selectedContent];

    // return the content it's original id
    currentContentState.contentId = originalContentId;
    newState['contents'][originalContentId] = currentContentState;

    // remove the blob with new id
    delete newState['contents'][newContentId];

    // remove connection to the offline content
    currentContentState.offlineBlobId = null;

    // update state properties referencing the id
    newState.selectedContent = originalContentId;
    newState.firstLiveContentId = currentContentState.queuePosition === 0 ? originalContentId : newState.firstLiveContentId;
    newState.totalNumberOfContents -= 1;

    // remove new id from change tracking arrays
    newState.newContents = deleteElementByValue(newState.newContents, newContentId);
    newState.updatedContents = deleteElementByValue(newState.updatedContents, newContentId);

    return newState;
};

export const seletedContentTypeChanged = selectedContentState => !!selectedContentState && !!selectedContentState.offlineBlobId && selectedContentState.originalType;

const _isQuestionBased = contentType => ['poll', 'quiz'].includes(contentType);

export const generateId = () => {
    return ([1e7] + -1e3).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 2).toString(16)
    )
};

export const noLiveContentCheck = content => {
    const liveContent = [];
    Object.keys(content).forEach(item => {
        if(content[item].contentLive){
            liveContent.push(item)
        }
    });
    return liveContent.length < 1
};

export const getTrimmedValue = (property, value) => (
    (property === 'title' || property === 'text') ? (value !== null && value.trim().length > 0) ? value.trim() : null : value
);
